import { useReducer, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { ModalDialogContext } from './modal-dialog-context';
import { modalDialogReducer, initialModalsState } from './modal-dialog.reducer';

const renderModalComponent = (modalData, ModalComponent, hideModal) => (
  <ModalComponent
    key={modalData.id}
    {...modalData.props}
    onCloseModal={() => {
      hideModal(modalData.id);
    }}
  />
);

/**
 * Modal Container is a wrapper that should be used at the application level.
 * Any child of the modal container can access the show modal and hide modal methods from modal context and display the modals registered in modalsList file.
 */
const ModalDialogContainer = ({
  modalsList,
  children,
}) => {
  const [state, dispatch] = useReducer(modalDialogReducer, {
    ...initialModalsState,
    modalsList,
  });

  const showModal = useCallback((id, props) => {
    dispatch({
      type: 'show-modal',
      payload: {
        id,
        props,
      },
    });
  }, []);

  const hideModal = useCallback((id) => {
    dispatch({
      type: 'hide-modal',
      payload: { id },
    });
  }, []);

  return (
    <>
      <ModalDialogContext.Provider
        value={{
          showModal,
          hideModal,
        }}
      >
        {children}
      </ModalDialogContext.Provider>
      <div data-ta="modal-dialog-container">
        {state.openModalsList.map((modal) => renderModalComponent(
          modal,
          modalsList[modal.id],
          hideModal,
        ))}
      </div>
    </>
  );
};

ModalDialogContainer.propTypes = {
  /** An object that contains all modal components in the application and its respective keys */
  modalsList: PropTypes.object.isRequired,
};

export default ModalDialogContainer;

/**
 * React hook to use the implementation of `showModal and hideModal` provided by `ModalDialogContainer`. By using
 * this hook, you can have modal anywhere you want in the applicaton.
*/

export const useModal = () => useContext(ModalDialogContext);
